import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Card';
import { SealBadge } from '../../components/ui/SealBadge';
import { api } from '../../lib/api';
import type { Order } from '@bharat-consultancy/shared';

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;

    // The Razorpay webhook (server-verified) is what actually marks the
    // order paid and generates the invoice — it can land a moment after
    // this client redirect fires. A short poll gives the page a chance
    // to show "Paid" instead of "Pending Payment" without the customer
    // needing to refresh manually.
    let attempts = 0;
    const maxAttempts = 6;
    const poll = () => {
      api.orders
        .get(orderId)
        .then((res) => {
          setOrder(res.order);
          if (res.order.status === 'pending_payment' && attempts < maxAttempts) {
            attempts += 1;
            setTimeout(poll, 1500);
          } else {
            setIsLoading(false);
          }
        })
        .catch(() => setIsLoading(false));
    };
    poll();
  }, [orderId]);

  return (
    <>
      <SEOHead title="Thank You — Bharat Consultancy" description="" canonicalPath="/booking/thank-you" />

      <section className="flex min-h-[70vh] items-center justify-center bg-paper px-4 py-12">
        <div className="w-full max-w-lg text-center">
          <div className="mx-auto w-fit">
            <SealBadge size="lg">
              <span className="block text-lg">✓</span>
              Filed &amp;
              <br />
              Approved
            </SealBadge>
          </div>

          <h1 className="mt-8 font-display text-3xl font-bold text-ink">Thank You for Choosing Bharat Consultancy</h1>

          {isLoading ? (
            <div className="mt-6 flex justify-center">
              <Spinner className="h-6 w-6 text-seal-blue" />
            </div>
          ) : (
            <>
              <p className="mt-3 text-ink/70">
                {order?.status === 'paid' || (order && order.status !== 'pending_payment')
                  ? 'Your payment was successful.'
                  : "We're confirming your payment — this page will update shortly."}
              </p>
              {order?.callbackDate && (
                <p className="mt-2 text-ink/70">
                  Our team will contact you on <strong>{order.callbackDate}</strong>
                  {order.callbackTime && (
                    <>
                      {' '}
                      at <strong>{order.callbackTime}</strong>
                    </>
                  )}
                  .
                </p>
              )}
              {order?.orderNumber && <p className="mt-2 font-mono text-sm text-ink/50">Order No: {order.orderNumber}</p>}
            </>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="tel:+918170896296">
              <Button variant="outline">Call Us</Button>
            </a>
            <a href="https://wa.me/918170896296" target="_blank" rel="noreferrer">
              <Button variant="outline">WhatsApp Us</Button>
            </a>
            <Link to="/dashboard">
              <Button>View Dashboard</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
